import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl } from '@angular/forms';
import { filter, Observable, ReplaySubject, take } from 'rxjs';

import { EntityTypeConstants } from 'src/app/common/EntityTypeConstants';
import { ExtendedProfile } from 'src/app/common/ExtendedProfile';
import { Setting } from 'src/app/common/Setting';
import { CallApiService } from '../../services/api-action.service';
import { SettingService } from '../../services/setting.service';

/** Customer-scoped rows are the account's, written by its admins; Profile-scoped ones the user's own. */
export type SettingScope = (typeof EntityTypeConstants)[keyof typeof EntityTypeConstants];

/** The two language pickers' entries — the reading language and the customer's outgoing one. */
export const languageOptions = () => [
  { code: 'de', name: $localize`:@@settings.language.de:Deutsch` },
  { code: 'en', name: $localize`:@@settings.language.en:Englisch` },
];

/**
 * The settings rows of one visit to the settings page, shared by its sections (#199).
 *
 * Each section edits a few rows of one scope, and two sections share each scope: printer and
 * notifications the Customer rows, user settings and personalization the Profile rows. They
 * used to be one component, which read each scope once and — after any save — patched every
 * form of that scope again, so saving the consent switch threw away a page size typed but not
 * yet saved. Now:
 *
 * - **One read per scope.** A scope is read when its first section asks for it, so a page
 *   without a Customer-scoped section (a non-admin's) never reads the Customer rows.
 * - **A re-read never overwrites an edit.** {@link patchOnRead} hands a section the first read
 *   always and a later one — the re-read after a save anywhere on the page — only while that
 *   section's form is pristine.
 * - **Rows are written as copies.** {@link row} never hands out the stored object, so a failed
 *   save cannot leave an unsaved value in what the page believes is stored.
 *
 * Provided by the settings page, which sets {@link profile} before any section is rendered.
 */
@Injectable()
export class SettingsPageStore {
  private readonly settingService = inject(SettingService);
  private readonly callApiService = inject(CallApiService);

  public profile: ExtendedProfile;

  private readonly reads = new Map<SettingScope, ReplaySubject<Setting[]>>();
  private readonly latest = new Map<SettingScope, Setting[]>();

  /**
   * Calls `patch` with every read of `scope` — the first always, a later one only while `form`
   * is pristine — and leaves the form pristine. Call it from a section's constructor: the
   * subscription ends with that section.
   */
  public patchOnRead(scope: SettingScope, form: AbstractControl, patch: () => void): void {
    this.read$(scope)
      .pipe(
        filter((_, index) => index === 0 || form.pristine),
        takeUntilDestroyed(inject(DestroyRef)),
      )
      .subscribe(() => {
        patch();
        form.markAsPristine();
      });
  }

  /** The value stored for `name` as of the latest read; undefined without a row. */
  public value(scope: SettingScope, name: string): string | undefined {
    return this.latest.get(scope)?.find((s) => s.name == name)?.value;
  }

  /**
   * The row to write: the stored one with its id (an update), or a new one without (a create —
   * profiles and customers created before a setting existed have no row yet).
   */
  public row(scope: SettingScope, name: string, value: string): Setting {
    const stored = this.latest.get(scope)?.find((s) => s.name == name);
    return { ...(stored ?? { name, referenceType: scope, referenceId: this.referenceId(scope) }), value };
  }

  /**
   * Writes one scope's rows. Never mix scopes in a batch: the backend rejects any batch holding
   * a Customer-scoped row from a non-admin, and one batch would couple the sections' failures.
   * Re-reading afterwards is the caller's job — after it has marked its own form pristine, so
   * the re-read reaches it, and not at all when the save navigates away.
   */
  public save$(batch: Setting[], confirmQuestion?: string): Observable<unknown> {
    const write$ = this.settingService.createOrUpdateMultiple(batch);
    const saved = $localize`Einstellungen wurden aktualisiert.`;
    const failed = $localize`Beim Aktualisieren der Einstellungen ist ein Fehler aufgetreten.`;
    return confirmQuestion
      ? this.callApiService.confirmAndCallWithToasts$(write$, confirmQuestion, saved, failed)
      : this.callApiService.callWithToasts$(write$, saved, failed);
  }

  /** Reads `scope` again — after a save, so a created row's id is known to the next one. */
  public reload(scope: SettingScope): void {
    this.settingService
      .getByReference(this.referenceId(scope), scope)
      .pipe(take(1))
      .subscribe((settings) => {
        this.latest.set(scope, settings ?? []);
        this.reads.get(scope).next(settings ?? []);
      });
  }

  private read$(scope: SettingScope): Observable<Setting[]> {
    if (!this.reads.has(scope)) {
      this.reads.set(scope, new ReplaySubject<Setting[]>(1));
      this.reload(scope);
    }
    return this.reads.get(scope);
  }

  private referenceId(scope: SettingScope): string {
    return scope == EntityTypeConstants.EntityTypeCustomer ? this.profile.customer.id : this.profile.id;
  }
}
